/**
 * F071 — single frontend source of truth for KB profile input caps.
 *
 * Mirrors the backend caps exactly:
 * - `app/services/onboarding/kb_profile_assembler.py::CAPS` (the by-construction
 *   size budget for the assembled `knowledge_md`), plus
 * - the schema-only caps from `app/schemas/tenant_kb_profile_schema.py` for
 *   fields that don't feed the assembled blob (link library, slot definitions).
 *
 * NEVER hardcode one of these numbers in a component — import from here. The
 * table-driven test in `enrichment/__tests__/editorCaps.test.tsx` fails on any
 * editor that drifts from this module, and the backend Marshmallow schema
 * re-enforces the same numbers on write (a drift = silent 422 on save).
 */
export const CAPS = {
  // --- kb_profile_assembler.py::CAPS (assembled-blob budget) ---------------
  one_liner: 200,
  ideal_customer: 200,
  // F073 rebalance: catalog/FAQ/policies/operation grown to fit the §9
  // persona (DECISIONS §9, amended 2026-06-11). faq_question stays 140.
  catalog_items: 15,
  catalog_name: 60,
  catalog_price: 30,
  catalog_description: 160,
  catalog_duration: 30,
  catalog_lead_time: 30,
  faq_items: 10,
  faq_question: 140,
  faq_answer: 300,
  operation_field: 120,
  payment_other_items: 6,
  payment_other: 40,
  closing_cta: 140,
  objective_freetext: 240,
  handoff_contact: 255,
  tone: 40,
  voice_example: 240,
  never_say: 220,
  forbidden_scope_items: 6,
  forbidden_scope: 60,
  persistent_disclaimer: 180,
  refer_external_destination: 80,
  differentiators: 240,
  // F075: "Quem conduz" — credentials of who delivers the service.
  team_credentials: 240,
  policies: 800,
  proactive_notice: 200,
  session_items: 30,
  session_note: 140,
  // F167: operational restrictions ("O que não fazemos") — 10×120.
  restriction_items: 10,
  restriction_text: 120,
  // --- tenant_kb_profile_schema.py (schema-only caps) ----------------------
  link_library_items: 30,
  link_label: 80,
  link_when_to_send: 200,
  slot_definitions_items: 40,
  slot_label: 120,
  slot_choice: 80,
  // Frontend-only sanity bound: the backend validates URL *format* (http/https),
  // not length — this just keeps the input from accepting unbounded paste.
  link_url: 500,
  // --- tenant profile (InfoSection / useTenantProfileForm, F072) -----------
  business_name: 80,
  owner_name: 80,
} as const;
